import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { FieldActivityLogModel } from '@core/models/models';
import {
    getHeaderItems
} from '@core/helper';
import moment from 'moment';
import { TimeService } from '@core/services/common/time.service';
import { Subscription } from 'rxjs';
import { ActivityLogType } from '@core/configs';
import { FieldActivityLogController } from '../field-activity-log-contoller';

interface HeaderItem {
    label: string;
    isIcon: boolean;
}

@Component({
    selector: 'caremoni-field-activity-header-month',
    templateUrl: './field-activity-header-month.component.html',
    styleUrl: './field-activity-header-month.component.scss'
})
export class FieldActivityHeaderMonthComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('parent', { static: true }) parentRef!: ElementRef;
    @ViewChild('child', { static: true }) childRef!: ElementRef;
    @ViewChild('pinnedElRef') pinnedElRef!: ElementRef;

    @Input() data: any[] = [];
    @Input() config: FieldActivityLogModel = new FieldActivityLogModel();

    headers: HeaderItem[] = [];
    width = '100%';
    positionHeader = 0;
    isViewInitialized = false;

    pinnedHeader: HeaderItem | null = null;
    pinnedIndex: number = -1;

    subscriberHandler: Subscription = new Subscription();

    positionGridSub!: Subscription;
    timeSub!: Subscription;
    changeHeaderSizedSub!: Subscription;


    constructor(
        private timeService: TimeService,
        private controller: FieldActivityLogController
    ) { }

    ngOnInit(): void {
        this.isViewInitialized = false;

        this.onTimeChange();

        this.width = `${(this.headers.length * 100) / this.config.segmentNum}%`;
        this.controller.setConfig(this.config);
        this.controller.setParantHeander(this.parentRef);

        const endhours = this.controller.getEndTime(this.timeService.getCurrentTime(), this.config);
        const potionGrid = this.controller.getPostionGrid(this.timeService.getCurrentTime(), this.config, endhours);
        this.positionHeader = this.getPositionHeader(potionGrid, this.config);

        this.subscriberHandler.add(this.controller.getChangePositionGrid().subscribe(this.onPostionGridChange.bind(this)));
        this.subscriberHandler.add(this.controller.getChangeTime().subscribe(this.onTimeChange.bind(this)));
        this.subscriberHandler.add(this.controller.getChangeSizeHeader().subscribe(() => {
            this.handerFirstAndLastHeader();
        }));
    }

    ngAfterViewInit(): void {
        this.handerFirstAndLastHeader();
    }

    ngOnDestroy(): void {
        this.subscriberHandler.unsubscribe();
    }

    onPostionGridChange(postion: any) {
        if (postion) {
            this.positionHeader = this.getPositionHeader(postion, this.config);
            this.handerFirstAndLastHeader();
        }
    }

    onTimeChange() {
        this.headers = getHeaderItems(this.config, this.timeService.getCurrentTime());
        this.headerMapping();
    }

    headerMapping() {
        // Take two time points
        const today = moment().format('MM/DD');
        const yesterday = moment().subtract(1, 'days').format('MM/DD');

        // Map icon and label to headers
        this.headers = this.headers.map((x: any, index) => {
            if (index === 1) return { label: yesterday, isIcon: false };
            if (x === 6) return { label: 'sunrise', isIcon: true };
            if (x === 12) return { label: 'sun', isIcon: true };
            if (x === 18) return { label: 'moon', isIcon: true };
            if (x === 0) return { label: today, isIcon: false };
            return { label: '', isIcon: false };
        });
    }

    getPositionHeader(postionGrid: any, config: any): any {
        if (postionGrid) {
            return this.config.type === ActivityLogType.LogCurrent
                ? postionGrid - 100 / this.config.segmentNum / 2
                : postionGrid;
        }
        return 0;
    }

    handerFirstAndLastHeader() {
        if (!this.parentRef || !this.childRef || this.config.type !== ActivityLogType.LogCurrent) return;
        
        // Get container and child elements
        const parent = this.parentRef.nativeElement;
        const child = this.childRef.nativeElement;
        const parentRect = parent.getBoundingClientRect();

        // Get pinned element and its rectangle
        const pinnedEl = this.pinnedElRef?.nativeElement;
        const pinnedRect = pinnedEl ? pinnedEl.getBoundingClientRect() : null;

        // Loop checking each header
        let anchorFlag = false;
        for (let i = 0; i < child.children.length; i++) {
            const header = this.headers[i];
            if (!header || header.isIcon || header.label === '') continue;

            const wrapper = child.children[i]?.children[0];
            if (!wrapper) continue;

            const rect = wrapper.getBoundingClientRect();

            // Based on the left position of the title, if the pinned element is present, 
            // move the anchor to the right value of the pinned element
            const anchor = parentRect.left;
            const isOverlappingPinned =
                (rect.left <= anchor && rect.right >= anchor); // Current header overlaps with pinned element

            if (isOverlappingPinned && !anchorFlag) {
                // Update pinned header and index
                this.pinnedHeader = header;
                this.pinnedIndex = i;
                anchorFlag = true;
            }
        }
    }

    /**
     * Check if the header should be hidden based on its index.
     * @param index - The index of the header to check.
     * @returns boolean - True if the header should be hidden, false otherwise.
     * @returns false if the parent, child, or pinned element references are not set,
     */
    shouldHideHeader(index: number): boolean {
        if (!this.parentRef || !this.childRef || !this.pinnedElRef || this.config.type !== ActivityLogType.LogCurrent) return false;

        const header = this.headers[index];
        if (!header || header.label === '') return false;

        const child = this.childRef.nativeElement;
        const wrapper = child.children[index]?.children[0];
        if (!wrapper) return false;

        const rect = wrapper.getBoundingClientRect();
        const pinnedRect = this.pinnedElRef.nativeElement.getBoundingClientRect();

        const overlap = rect.left < pinnedRect.right && rect.right > pinnedRect.left;

        if (overlap && !header.isIcon && header.label != '') {
            this.pinnedHeader = header;
        }

        return overlap;
    }

}
